import React, { useState, useEffect, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Briefcase, MapPin, Clock, IndianRupee, Search, ArrowLeft, Upload, CheckCircle } from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Default Recruitizr branding (used when no tenant slug is provided)
const DEFAULT_BRAND = {
  name: 'Recruitizr',
  tagline: 'Find your next opportunity.',
  logo_url: '',
  primary_color: '#1e3a8a', // blue-900
};

export default function CareersPage() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [brand, setBrand] = useState(DEFAULT_BRAND);
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchText, setSearchText] = useState('');
  const [filterDept, setFilterDept] = useState('all');
  const [filterLocation, setFilterLocation] = useState('all');
  const [selectedJob, setSelectedJob] = useState(null);
  const [applyOpen, setApplyOpen] = useState(false);
  const [applied, setApplied] = useState(false);
  const [form, setForm] = useState({ candidate_name: '', email: '', phone: '', cover_letter: '' });
  const [file, setFile] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    const fetchAll = async () => {
      try {
        if (slug) {
          const [tRes, jRes] = await Promise.all([
            axios.get(`${API}/public/tenants/${slug}`).catch(() => null),
            axios.get(`${API}/public/jobs?tenant_slug=${encodeURIComponent(slug)}`),
          ]);
          if (tRes?.data) {
            setBrand({
              name: tRes.data.name || DEFAULT_BRAND.name,
              tagline: tRes.data.tagline || `Join ${tRes.data.name}`,
              logo_url: tRes.data.logo_url || '',
              primary_color: tRes.data.primary_color || DEFAULT_BRAND.primary_color,
            });
            // Swap browser tab favicon to the tenant's uploaded favicon. Remove
            // all icon-related <link> tags shipped in index.html so candidates
            // see the tenant's brand in the tab, not Recruitizr's logo.
            if (tRes.data.favicon_url) {
              document.querySelectorAll(
                "link[rel='icon'], link[rel='shortcut icon'], link[rel='apple-touch-icon'], link[rel='mask-icon']"
              ).forEach(n => n.parentNode && n.parentNode.removeChild(n));
              const link = document.createElement('link');
              link.rel = 'icon';
              link.href = tRes.data.favicon_url + (tRes.data.favicon_url.includes('?') ? '&' : '?') + 'v=' + Date.now();
              document.head.appendChild(link);
            }
            // Update tab title to reflect tenant brand
            if (tRes.data.name) {
              document.title = `Careers \u2014 ${tRes.data.name}`;
            }
          }
          setJobs(jRes.data || []);
        } else {
          const res = await axios.get(`${API}/public/jobs`);
          setJobs(res.data || []);
        }
      } catch { /* silent */ }
      finally { setLoading(false); }
    };
    fetchAll();
  }, [slug]);

  const departments = useMemo(() => [...new Set(jobs.map(j => j.department).filter(Boolean))].sort(), [jobs]);
  const locations = useMemo(() => [...new Set(jobs.map(j => j.location).filter(Boolean))].sort(), [jobs]);

  const filtered = useMemo(() => {
    return jobs.filter(j => {
      if (searchText) {
        const q = searchText.toLowerCase();
        if (!(j.title?.toLowerCase().includes(q) || j.department?.toLowerCase().includes(q) || j.location?.toLowerCase().includes(q) || j.description?.toLowerCase().includes(q))) return false;
      }
      if (filterDept !== 'all' && j.department !== filterDept) return false;
      if (filterLocation !== 'all' && j.location !== filterLocation) return false;
      return true;
    });
  }, [jobs, searchText, filterDept, filterLocation]);

  const timeAgo = (dateStr) => {
    if (!dateStr) return '';
    const diff = Date.now() - new Date(dateStr).getTime();
    const days = Math.floor(diff / 86400000);
    if (days === 0) return 'Today';
    if (days === 1) return 'Yesterday';
    if (days < 7) return `${days} days ago`;
    if (days < 30) return `${Math.floor(days / 7)} weeks ago`;
    return `${Math.floor(days / 30)} months ago`;
  };

  const handleApply = async (e) => {
    e.preventDefault();
    if (!form.candidate_name || !form.email) { toast.error('Name and email are required'); return; }
    setSubmitting(true);
    try {
      const formData = new FormData();
      formData.append('candidate_name', form.candidate_name);
      formData.append('email', form.email);
      formData.append('phone', form.phone);
      formData.append('cover_letter', form.cover_letter);
      if (file) formData.append('file', file);
      await axios.post(
        `${API}/public/jobs/${selectedJob.id}/apply?candidate_name=${encodeURIComponent(form.candidate_name)}&email=${encodeURIComponent(form.email)}&phone=${encodeURIComponent(form.phone)}&cover_letter=${encodeURIComponent(form.cover_letter)}${slug ? `&tenant_slug=${encodeURIComponent(slug)}` : ''}`,
        formData,
        { headers: { 'Content-Type': 'multipart/form-data' } }
      );
      setApplied(true);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to submit application');
    } finally { setSubmitting(false); }
  };

  const resetApply = () => {
    setApplyOpen(false); setApplied(false);
    setForm({ candidate_name: '', email: '', phone: '', cover_letter: '' });
    setFile(null);
  };

  // Job Detail View
  if (selectedJob && !applyOpen) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-slate-50 to-white">
        <header className="bg-white border-b border-slate-200 sticky top-0 z-10">
          <div className="max-w-4xl mx-auto px-6 py-4 flex items-center justify-between">
            <div className="flex items-center gap-3">
              {brand.logo_url ? (
                <img src={brand.logo_url} alt={`${brand.name} Logo`} className="h-12 w-12 object-contain" />
              ) : (
                <div className="h-12 w-12 rounded-lg flex items-center justify-center text-white font-extrabold text-xl" style={{ backgroundColor: brand.primary_color }}>
                  {brand.name.charAt(0)}
                </div>
              )}
              <span className="text-xl font-extrabold" style={{ fontFamily: 'Manrope, sans-serif', color: brand.primary_color }}>Careers @ {brand.name}</span>
            </div>
            <button data-testid="back-to-jobs" onClick={() => setSelectedJob(null)} className="flex items-center gap-1 text-sm text-blue-600 hover:text-blue-800 font-bold">
              <ArrowLeft size={16} />All Jobs
            </button>
          </div>
        </header>
        <main className="max-w-4xl mx-auto px-6 py-10">
          <h1 className="text-3xl sm:text-4xl font-extrabold text-blue-900 mb-3" data-testid="job-detail-title" style={{ fontFamily: 'Manrope, sans-serif' }}>{selectedJob.title}</h1>
          <div className="flex flex-wrap gap-4 text-sm text-slate-600 mb-8">
            <span className="flex items-center gap-1.5 bg-blue-50 px-3 py-1.5 rounded-full"><Briefcase size={14} className="text-blue-600" />{selectedJob.department}</span>
            <span className="flex items-center gap-1.5 bg-blue-50 px-3 py-1.5 rounded-full"><MapPin size={14} className="text-blue-600" />{selectedJob.location}</span>
            {(selectedJob.salary_min || selectedJob.salary_max) && (
              <span className="flex items-center gap-1.5 bg-emerald-50 px-3 py-1.5 rounded-full text-emerald-800">
                <IndianRupee size={14} />{selectedJob.salary_min ? `${(selectedJob.salary_min/100000).toFixed(1)}L` : '—'} – {selectedJob.salary_max ? `${(selectedJob.salary_max/100000).toFixed(1)}L` : '—'}
              </span>
            )}
            <span className="flex items-center gap-1.5 text-slate-500"><Clock size={14} />{timeAgo(selectedJob.created_at)}</span>
          </div>
          {selectedJob.description && (
            <section className="mb-8">
              <h2 className="text-lg font-bold text-blue-900 mb-3">About the Role</h2>
              <p className="text-slate-700 leading-relaxed whitespace-pre-wrap">{selectedJob.description}</p>
            </section>
          )}
          {selectedJob.requirements && (
            <section className="mb-8">
              <h2 className="text-lg font-bold text-blue-900 mb-3">Requirements</h2>
              <p className="text-slate-700 leading-relaxed whitespace-pre-wrap">{selectedJob.requirements}</p>
            </section>
          )}
          <button data-testid="apply-now-detail-btn" onClick={() => setApplyOpen(true)}
            className="w-full sm:w-auto px-10 py-4 bg-blue-600 hover:bg-blue-700 text-white font-bold rounded-lg text-lg transition-colors">
            Apply Now
          </button>
        </main>
      </div>
    );
  }

  // Application Form
  if (applyOpen && selectedJob) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-slate-50 to-white">
        <header className="bg-white border-b border-slate-200 sticky top-0 z-10">
          <div className="max-w-2xl mx-auto px-6 py-4 flex items-center justify-between">
            <div className="flex items-center gap-3">
              {brand.logo_url ? (
                <img src={brand.logo_url} alt={`${brand.name} Logo`} className="h-12 w-12 object-contain" />
              ) : (
                <div className="h-12 w-12 rounded-lg flex items-center justify-center text-white font-extrabold text-xl" style={{ backgroundColor: brand.primary_color }}>
                  {brand.name.charAt(0)}
                </div>
              )}
              <span className="text-xl font-extrabold" style={{ fontFamily: 'Manrope, sans-serif', color: brand.primary_color }}>Careers @ {brand.name}</span>
            </div>
            <button data-testid="back-to-detail" onClick={resetApply} className="flex items-center gap-1 text-sm text-blue-600 hover:text-blue-800 font-bold">
              <ArrowLeft size={16} />Back
            </button>
          </div>
        </header>
        <main className="max-w-2xl mx-auto px-6 py-10">
          {applied ? (
            <div className="text-center py-16" data-testid="apply-success">
              <CheckCircle size={64} className="mx-auto mb-4 text-emerald-500" />
              <h2 className="text-2xl font-bold text-blue-900 mb-2">Application Submitted!</h2>
              <p className="text-slate-600 mb-6">Thank you for applying to <strong>{selectedJob.title}</strong>. We'll review your application and get back to you soon.</p>
              <button onClick={() => { resetApply(); setSelectedJob(null); }} className="px-8 py-3 bg-blue-600 hover:bg-blue-700 text-white font-bold rounded-lg transition-colors">
                Browse More Jobs
              </button>
            </div>
          ) : (
            <>
              <h1 className="text-2xl font-extrabold text-blue-900 mb-1" style={{ fontFamily: 'Manrope, sans-serif' }}>Apply for {selectedJob.title}</h1>
              <p className="text-slate-500 mb-6">{selectedJob.department} — {selectedJob.location}</p>
              <form onSubmit={handleApply} className="space-y-5 bg-white border border-slate-200 rounded-lg p-6 shadow-sm">
                <div>
                  <label className="text-xs font-bold uppercase tracking-wider text-blue-900 block mb-1.5">Full Name *</label>
                  <input data-testid="apply-name" value={form.candidate_name} onChange={e => setForm({ ...form, candidate_name: e.target.value })} required
                    className="w-full h-11 rounded-lg border border-slate-300 px-4 text-sm focus:border-blue-500 focus:ring-1 focus:ring-blue-500 outline-none" placeholder="Your full name" />
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label className="text-xs font-bold uppercase tracking-wider text-blue-900 block mb-1.5">Email *</label>
                    <input data-testid="apply-email" type="email" value={form.email} onChange={e => setForm({ ...form, email: e.target.value })} required
                      className="w-full h-11 rounded-lg border border-slate-300 px-4 text-sm focus:border-blue-500 focus:ring-1 focus:ring-blue-500 outline-none" placeholder="your@email.com" />
                  </div>
                  <div>
                    <label className="text-xs font-bold uppercase tracking-wider text-blue-900 block mb-1.5">Phone</label>
                    <input data-testid="apply-phone" value={form.phone} onChange={e => setForm({ ...form, phone: e.target.value })}
                      className="w-full h-11 rounded-lg border border-slate-300 px-4 text-sm focus:border-blue-500 focus:ring-1 focus:ring-blue-500 outline-none" placeholder="+91-XXXXXXXXXX" />
                  </div>
                </div>
                <div>
                  <label className="text-xs font-bold uppercase tracking-wider text-blue-900 block mb-1.5">Resume</label>
                  <div className="border-2 border-dashed border-slate-300 rounded-lg p-4 text-center hover:border-blue-400 transition-colors cursor-pointer" onClick={() => document.getElementById('resume-input').click()}>
                    <Upload size={24} className="mx-auto mb-1 text-slate-400" />
                    <p className="text-sm text-slate-500">{file ? file.name : 'Click to upload (PDF, DOC, DOCX)'}</p>
                    <input id="resume-input" data-testid="apply-resume" type="file" accept=".pdf,.doc,.docx" onChange={e => setFile(e.target.files[0])} className="hidden" />
                  </div>
                </div>
                <div>
                  <label className="text-xs font-bold uppercase tracking-wider text-blue-900 block mb-1.5">Cover Letter</label>
                  <textarea data-testid="apply-cover" value={form.cover_letter} onChange={e => setForm({ ...form, cover_letter: e.target.value })} rows={4}
                    className="w-full rounded-lg border border-slate-300 px-4 py-3 text-sm focus:border-blue-500 focus:ring-1 focus:ring-blue-500 outline-none" placeholder="Tell us why you're a great fit..." />
                </div>
                <button type="submit" data-testid="submit-application-btn" disabled={submitting}
                  className="w-full py-3.5 bg-blue-600 hover:bg-blue-700 text-white font-bold rounded-lg text-base transition-colors disabled:opacity-50">
                  {submitting ? 'Submitting...' : 'Submit Application'}
                </button>
              </form>
            </>
          )}
        </main>
      </div>
    );
  }

  // Job Listing View
  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-50 to-white">
      {/* Header */}
      <header className="bg-white border-b border-slate-200 sticky top-0 z-10">
        <div className="max-w-6xl mx-auto px-6 py-4 flex items-center gap-4">
          {brand.logo_url ? (
            <img src={brand.logo_url} alt={`${brand.name} Logo`} className="h-14 w-14 object-contain" />
          ) : (
            <div className="h-14 w-14 rounded-lg flex items-center justify-center text-white font-extrabold text-2xl" style={{ backgroundColor: brand.primary_color }}>
              {brand.name.charAt(0)}
            </div>
          )}
          <h1 className="text-2xl font-extrabold" style={{ fontFamily: 'Manrope, sans-serif', color: brand.primary_color }}>Careers @ {brand.name}</h1>
        </div>
      </header>

      {/* Hero */}
      <section className="text-white py-16 sm:py-20" style={{ background: `linear-gradient(135deg, ${brand.primary_color}, ${brand.primary_color}ee 50%, ${brand.primary_color})` }}>
        <div className="max-w-6xl mx-auto px-6 text-center">
          <h2 className="text-3xl sm:text-5xl font-extrabold mb-4" data-testid="careers-hero-title" style={{ fontFamily: 'Manrope, sans-serif' }}>Find the career of your dreams.</h2>
          <p className="text-lg sm:text-xl text-white/90 font-bold mb-3">{brand.tagline}</p>
          <p className="text-base sm:text-lg text-white/80 max-w-3xl mx-auto mb-8 leading-relaxed">
            {slug
              ? `Explore open roles at ${brand.name}. We're hiring across multiple departments and locations — find a role that matches your skills and start your journey with us.`
              : `We know that finding a meaningful and rewarding job can be a long journey. Our goal is to make that process as easy as possible for you by assisting in your Job Search. A Job where you'll look forward to coming to every day. Start your journey by browsing available jobs with our clients.`}
          </p>
          <div className="max-w-xl mx-auto relative">
            <Search size={18} className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400" />
            <input data-testid="careers-search" value={searchText} onChange={e => setSearchText(e.target.value)}
              className="w-full h-12 rounded-full pl-11 pr-4 text-slate-900 text-sm focus:ring-2 focus:ring-blue-400 outline-none shadow-lg" placeholder="Search roles, departments, locations..." />
          </div>
        </div>
      </section>

      {/* Filters + Jobs */}
      <main className="max-w-6xl mx-auto px-6 py-10">
        <div className="flex flex-wrap items-center gap-3 mb-8">
          <select data-testid="careers-filter-dept" value={filterDept} onChange={e => setFilterDept(e.target.value)} className="h-10 rounded-lg border border-slate-300 bg-white px-4 text-sm">
            <option value="all">All Departments</option>
            {departments.map(d => <option key={d} value={d}>{d}</option>)}
          </select>
          <select data-testid="careers-filter-location" value={filterLocation} onChange={e => setFilterLocation(e.target.value)} className="h-10 rounded-lg border border-slate-300 bg-white px-4 text-sm">
            <option value="all">All Locations</option>
            {locations.map(l => <option key={l} value={l}>{l}</option>)}
          </select>
          <span className="text-sm text-slate-500 ml-2" data-testid="careers-count">{filtered.length} open position{filtered.length !== 1 ? 's' : ''}</span>
        </div>

        {loading ? (
          <div className="text-center py-16 text-slate-500">Loading opportunities...</div>
        ) : filtered.length === 0 ? (
          <div className="text-center py-16">
            <Briefcase size={56} className="mx-auto mb-4 text-slate-300" />
            <p className="text-slate-500 text-lg" data-testid="no-careers-msg">No open positions at the moment</p>
            <p className="text-slate-400 text-sm mt-1">Check back soon — we're always growing!</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
            {filtered.map(job => (
              <div key={job.id} data-testid={`career-card-${job.id}`}
                onClick={() => {
                  // Route to the dedicated per-job permalink. Falls back to
                  // in-page detail for older payloads without url_slug.
                  if (slug && job.url_slug) {
                    navigate(`/careers/${slug}/${job.url_slug}`);
                  } else {
                    setSelectedJob(job);
                  }
                }}
                className="bg-white border border-slate-200 rounded-xl p-6 cursor-pointer hover:shadow-lg hover:border-blue-300 transition-all group">
                <div className="flex items-start justify-between mb-3">
                  <h3 className="text-lg font-bold text-blue-900 group-hover:text-blue-700 transition-colors">{job.title}</h3>
                </div>
                <div className="flex flex-wrap gap-2 mb-3">
                  <span className="flex items-center gap-1 text-xs text-slate-600 bg-slate-100 px-2.5 py-1 rounded-full"><Briefcase size={11} />{job.department}</span>
                  <span className="flex items-center gap-1 text-xs text-slate-600 bg-slate-100 px-2.5 py-1 rounded-full"><MapPin size={11} />{job.location}</span>
                </div>
                {job.description && (
                  <p className="text-sm text-slate-600 mb-4 line-clamp-2">{job.description.slice(0, 120)}{job.description.length > 120 ? '...' : ''}</p>
                )}
                <div className="flex items-center justify-between">
                  {(job.salary_min || job.salary_max) ? (
                    <span className="flex items-center gap-0.5 text-xs text-emerald-700 font-bold"><IndianRupee size={11} />{job.salary_min ? `${(job.salary_min/100000).toFixed(1)}L` : '—'} – {job.salary_max ? `${(job.salary_max/100000).toFixed(1)}L` : '—'}</span>
                  ) : <span />}
                  <span className="text-xs text-slate-400">{timeAgo(job.created_at)}</span>
                </div>
                <button data-testid={`apply-btn-${job.id}`}
                  onClick={(e) => {
                    e.stopPropagation();
                    if (slug && job.url_slug) {
                      navigate(`/careers/${slug}/${job.url_slug}#apply`);
                    } else {
                      setSelectedJob(job); setApplyOpen(true);
                    }
                  }}
                  className="w-full mt-4 py-2.5 bg-blue-600 hover:bg-blue-700 text-white font-bold rounded-lg text-sm transition-colors">
                  Apply Now
                </button>
              </div>
            ))}
          </div>
        )}
      </main>

      {/* Footer */}
      <footer className="border-t border-slate-200 py-8 text-center text-sm text-slate-500">
        <p>&copy; {new Date().getFullYear()} {brand.name}. All rights reserved.</p>
      </footer>
    </div>
  );
}
